import * as api from './source/api.js';
const modeFromBuild = typeof __ZALSO_BACKOFFICE_AUTH_MODE__ === 'string'
  ? __ZALSO_BACKOFFICE_AUTH_MODE__
  : 'preview';

export const authMode = api.live || modeFromBuild === 'live' ? 'live' : 'preview';

export function liveOriginAllowed(locationValue = window.location) {
  if (locationValue.protocol === 'https:') return true;
  return locationValue.protocol === 'http:' && ['127.0.0.1', 'localhost', '::1', '[::1]'].includes(locationValue.hostname);
}

async function request(path, options = {}) {
  return api.request(path.replace('/api/v1/backoffice',''), {
    method:options.method, body:options.body ? api.parseJson(options.body) : undefined,
    key:options.headers?.['Idempotency-Key'], auth:true,
  });
}

function contractError(code = 'service_unavailable') {
  const error = new api.ApiError(0, {detail:'Die Serverantwort ist unvollständig. Bitte denselben Versuch erneut prüfen.'});
  error.code = code;
  return error;
}

function statusBody(result, expected) {
  return result.status === 200 && result.body && typeof result.body === 'object' &&
    !Array.isArray(result.body) && result.body.status === expected &&
    Object.keys(result.body).length === 1;
}

export async function activationSummary(token) {
  const result = await request('/api/v1/backoffice/activation/summary', {
    method:'POST', body:JSON.stringify({token}),
  });
  const summary = result.body, contract = summary?.contract;
  const text = value => typeof value === 'string' && value.trim().length > 0;
  const cents = value => Number.isSafeInteger(value) && value >= 0;
  const months = value => Number.isSafeInteger(value) && value >= 0;
  const documentAddress = value => {
    if (!text(value) || value !== value.trim() || value.length > 2048 || /[{}]/.test(value)) return false;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && url.hostname.length > 0 && !url.username && !url.password;
    } catch (_) { return false; }
  };
  if (result.status !== 200 || !summary || !text(summary.merchant?.displayName) ||
      !text(summary.merchant?.legalName) || !text(contract?.contractId) || !text(contract?.termModel) ||
      !cents(contract.monthlyPriceNetCents) || !(contract.individualPriceNetCents === null || cents(contract.individualPriceNetCents)) ||
      !(contract.freeMonths === null || months(contract.freeMonths)) ||
      !(contract.freeUntil === null || (text(contract.freeUntil) && Number.isFinite(Date.parse(contract.freeUntil)))) ||
      // Paket N19: optional (an older answer has none), null allowed; display only, never part of the hash.
      !(contract.serviceStartDate == null || (typeof contract.serviceStartDate === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(contract.serviceStartDate) && Number.isFinite(Date.parse(contract.serviceStartDate)))) ||
      !(contract.billingStartsAt == null || (text(contract.billingStartsAt) && Number.isFinite(Date.parse(contract.billingStartsAt)))) ||
      !(summary.merchant.timeZone == null || text(summary.merchant.timeZone)) ||
      typeof contract.tseArchiveIncluded !== 'boolean' || !text(summary.termsVersion) ||
      !text(summary.dataProcessingVersion) || !documentAddress(summary.termsUrl) ||
      !documentAddress(summary.dataProcessingUrl) || typeof summary.contractSummaryHash !== 'string' ||
      !/^[a-f\d]{64}$/i.test(summary.contractSummaryHash) || !text(summary.expiresAt) ||
      !Number.isFinite(Date.parse(summary.expiresAt))) throw contractError();
  return summary;
}

export async function activate(token, password, acceptance) {
  const result = await request('/api/v1/backoffice/activation', {
    method: 'POST',
    body: JSON.stringify({token, password, acceptance}),
  });
  if (!statusBody(result, 'activated')) throw contractError('activation_uncertain');
}

export async function login(email, password, idempotencyKey) {
  try {
    const result = await request('/api/v1/backoffice/login', {
      method: 'POST',
      headers: {'Idempotency-Key': idempotencyKey},
      body: JSON.stringify({email, password}),
    });
    if (!statusBody(result, 'authenticated')) throw contractError();
  } catch (error) {
    error.message=loginError(error);
    throw error;
  }
}

export function loginError(error) {
  if(error.status===429)return 'Zu viele Versuche. Bitte warte und versuche es später erneut.';
  if(error.status===503)return 'Die Anmeldung ist gerade nicht erreichbar. Es wurde nichts automatisch wiederholt. Du kannst denselben Versuch erneut absenden.';
  if(error.status===409)return 'Dieser Anmeldeversuch kann nicht sicher fortgesetzt werden. Bitte prüfe den Hinweis und die Eingaben.';
  if(error.status===401)return 'E-Mail oder Passwort stimmen nicht.';
  if(!error.status)return 'Die Antwort ist ausgeblieben. Es wurde nichts automatisch wiederholt. Du kannst denselben Versuch ausdrücklich erneut absenden.';
  return error.message;
}

export async function session() {
  const result = await request('/api/v1/backoffice/session');
  const body = result.body;
  if (result.status !== 200 || !body || typeof body !== 'object' || Array.isArray(body) ||
    !Array.isArray(body.memberships) ||
    !body.memberships.every(item => item &&
      typeof item.id === 'string' && typeof item.merchantId === 'string' &&
      typeof item.merchantLabel === 'string' && typeof item.roleCode === 'string')) throw contractError();
  return {memberships: body.memberships};
}

export async function logout() {
  const result = await request('/api/v1/backoffice/logout', {method: 'POST', body: '{}'});
  if (result.status !== 204 || result.body !== null) throw contractError();
}
